import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export const WORK_MINUTES = 25;
export const SHORT_BREAK_MINUTES = 5;
export const LONG_BREAK_MINUTES = 15;
export const SESSIONS_BEFORE_LONG_BREAK = 4;
const STORAGE_KEY = "skilll_focus_without_phone_v1";

export const FOCUS_STAGE_META = {
  work: {
    label: "Deep work block",
    minutes: WORK_MINUTES,
    tone: "#f59e0b",
    soft: "rgba(245, 158, 11, 0.18)",
    gradient: "linear-gradient(90deg, #f59e0b 0%, #38bdf8 100%)",
    hint: "Put the phone away. Work on one thing only until the block ends.",
  },
  shortBreak: {
    label: "Short break",
    minutes: SHORT_BREAK_MINUTES,
    tone: "#22c55e",
    soft: "rgba(34, 197, 94, 0.18)",
    gradient: "linear-gradient(90deg, #22c55e 0%, #38bdf8 100%)",
    hint: "Stand up, breathe, sip water, and do not open random apps.",
  },
  longBreak: {
    label: "Long break",
    minutes: LONG_BREAK_MINUTES,
    tone: "#38bdf8",
    soft: "rgba(56, 189, 248, 0.18)",
    gradient: "linear-gradient(90deg, #38bdf8 0%, #6366f1 100%)",
    hint: "You earned a longer reset. Come back with a clear next target.",
  },
};

const FocusTimerContext = createContext(null);

const createInitialState = () => ({
  stage: "work",
  remainingSeconds: WORK_MINUTES * 60,
  isRunning: false,
  completedWorkSessions: 0,
  todayCompletedSessions: 0,
  activity: [],
});

const getTodayKey = () => new Date().toISOString().slice(0, 10);

const loadStoredState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return createInitialState();

    const parsed = JSON.parse(raw);
    const todayKey = getTodayKey();

    if (parsed.todayKey !== todayKey) {
      return {
        ...createInitialState(),
        activity: [],
      };
    }

    return {
      ...createInitialState(),
      ...parsed,
    };
  } catch {
    return createInitialState();
  }
};

export const formatFocusTime = (seconds) => {
  const mins = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const secs = (seconds % 60).toString().padStart(2, "0");
  return `${mins}:${secs}`;
};

export function FocusTimerProvider({ children }) {
  const [state, setState] = useState(createInitialState);

  useEffect(() => {
    setState(loadStoredState());
  }, []);

  useEffect(() => {
    const payload = {
      ...state,
      todayKey: getTodayKey(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }, [state]);

  useEffect(() => {
    if (!state.isRunning) return undefined;

    const timer = window.setInterval(() => {
      setState((prev) => {
        if (!prev.isRunning) return prev;

        if (prev.remainingSeconds <= 1) {
          const finishedStage = prev.stage;

          if (finishedStage === "work") {
            const completedWorkSessions = prev.completedWorkSessions + 1;
            const nextStage =
              completedWorkSessions % SESSIONS_BEFORE_LONG_BREAK === 0
                ? "longBreak"
                : "shortBreak";

            return {
              ...prev,
              stage: nextStage,
              remainingSeconds: FOCUS_STAGE_META[nextStage].minutes * 60,
              isRunning: false,
              completedWorkSessions,
              todayCompletedSessions: prev.todayCompletedSessions + 1,
              activity: [
                {
                  id: Date.now(),
                  label: "Completed 25-minute focus block",
                  timestamp: new Date().toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
                },
                ...prev.activity,
              ].slice(0, 8),
            };
          }

          return {
            ...prev,
            stage: "work",
            remainingSeconds: WORK_MINUTES * 60,
            isRunning: false,
            activity: [
              {
                id: Date.now(),
                label:
                  finishedStage === "longBreak"
                    ? "Finished long break"
                    : "Finished short break",
                timestamp: new Date().toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
              },
              ...prev.activity,
            ].slice(0, 8),
          };
        }

        return {
          ...prev,
          remainingSeconds: prev.remainingSeconds - 1,
        };
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [state.isRunning]);

  const totalSeconds = FOCUS_STAGE_META[state.stage].minutes * 60;
  const progressPercent = useMemo(() => {
    const completed = totalSeconds - state.remainingSeconds;
    return Math.max(0, Math.min(100, (completed / totalSeconds) * 100));
  }, [state.remainingSeconds, totalSeconds]);

  const nextMilestone = useMemo(() => {
    const leftToLongBreak =
      SESSIONS_BEFORE_LONG_BREAK -
      (state.completedWorkSessions % SESSIONS_BEFORE_LONG_BREAK ||
        SESSIONS_BEFORE_LONG_BREAK);

    if (state.stage !== "work") {
      return "Complete the break and get back into one clean work block.";
    }

    if (leftToLongBreak === SESSIONS_BEFORE_LONG_BREAK) {
      return "Complete this block and you start building your next long-break cycle.";
    }

    return `${leftToLongBreak} focused block${
      leftToLongBreak > 1 ? "s" : ""
    } until your long break.`;
  }, [state.completedWorkSessions, state.stage]);

  const setStage = (stage) =>
    setState((prev) => ({
      ...prev,
      stage,
      remainingSeconds: FOCUS_STAGE_META[stage].minutes * 60,
      isRunning: false,
    }));

  const toggleTimer = () =>
    setState((prev) => ({
      ...prev,
      isRunning: !prev.isRunning,
    }));

  const resetCurrentStage = () =>
    setState((prev) => ({
      ...prev,
      isRunning: false,
      remainingSeconds: FOCUS_STAGE_META[prev.stage].minutes * 60,
    }));

  const skipStage = () =>
    setState((prev) => {
      if (prev.stage === "work") {
        return {
          ...prev,
          stage: "shortBreak",
          remainingSeconds: SHORT_BREAK_MINUTES * 60,
          isRunning: false,
        };
      }

      return {
        ...prev,
        stage: "work",
        remainingSeconds: WORK_MINUTES * 60,
        isRunning: false,
      };
    });

  const showFloatingTimer =
    state.isRunning ||
    state.remainingSeconds !== WORK_MINUTES * 60 ||
    state.todayCompletedSessions > 0;

  return (
    <FocusTimerContext.Provider
      value={{
        state,
        stageMeta: FOCUS_STAGE_META[state.stage],
        progressPercent,
        nextMilestone,
        showFloatingTimer,
        setStage,
        toggleTimer,
        resetCurrentStage,
        skipStage,
      }}
    >
      {children}
    </FocusTimerContext.Provider>
  );
}

export const useFocusTimer = () => {
  const value = useContext(FocusTimerContext);
  if (!value) {
    throw new Error("useFocusTimer must be used within FocusTimerProvider");
  }
  return value;
};
